import { useState, useEffect } from "react";
import { useUsers } from "../../../../hooks/masters/useUsers";
import { generatePassword } from "../../../../utils/pass";
import {
  Page, Left, Right, HeaderRow,
  RoleListContainer, RoleItem, SearchInput,
} from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";
import SearchableSelect from "../../../../components/Common/SearchableSelect";
import sedeApi from "../../../../api/sedeApi";
import { toast } from "react-toastify";

/* ─── Acordeón (idéntico al de ReportManager) ─── */
function Accordion({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "6px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
      <button onClick={() => setOpen(!open)} style={{
        width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "5px 10px", background: open ? "#f1f5f9" : "#f8fafc",
        border: "none", borderRadius: open ? "6px 6px 0 0" : "6px",
        cursor: "pointer", fontSize: "11px", fontWeight: "600", color: "#1e40af",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {title}
          {count != null && (
            <span style={{ background: "#dbeafe", color: "#2563eb", borderRadius: "10px", padding: "0 6px", fontSize: "10px", fontWeight: "700" }}>
              {count}
            </span>
          )}
        </span>
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "6px 8px", borderTop: "1px solid #e2e8f0" }}>{children}</div>}
    </div>
  );
}

/* ─── Row label + field (idéntico al de ReportManager) ─── */
function Row({ lbl, children, required }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
      <label style={{ minWidth: "110px", fontSize: "11px", color: "#64748b", textAlign: "right", flexShrink: 0 }}>
        {lbl}{required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/* ─── Field: label arriba + input abajo (para grid 2 columnas) ─── */
function Field({ lbl, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
        {lbl}{required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/* ─── Componente principal ─── */
export default function Users() {
  const {
    users, roles, search, setSearch, selectedUser,
    showPass, setShowPass, form, setForm,
    handleNew, handleSelectUser, saveUser,
  } = useUsers();

  const [todasSedes, setTodasSedes]   = useState([]);
  const [sedesUsuario, setSedesUsuario] = useState([]);
  const [sedeAgg, setSedeAgg]         = useState("");

  useEffect(() => {
    sedeApi.getAll().then((rs) => setTodasSedes(rs.data || []));
  }, []);

  useEffect(() => {
    if (form.id) {
      sedeApi.getByUsuario(form.id).then((rs) => setSedesUsuario(rs.data || []));
    } else {
      setSedesUsuario([]);
      setSedeAgg("");
    }
  }, [form.id]);

  const handleAgregarSede = async () => {
    if (!sedeAgg) return;
    const rs = await sedeApi.asignar(form.id, sedeAgg);
    if (rs.success) {
      toast.success("Sede asignada");
      sedeApi.getByUsuario(form.id).then((r) => setSedesUsuario(r.data || []));
      setSedeAgg("");
    } else {
      toast.error(rs.message || "Error");
    }
  };

  const handleQuitarSede = async (idAsignacion) => {
    const rs = await sedeApi.quitar(idAsignacion);
    if (rs.success) {
      toast.success("Sede removida");
      setSedesUsuario((prev) => prev.filter((s) => s.id !== idAsignacion));
    } else {
      toast.error(rs.message || "Error");
    }
  };

  const sedesAsignadasIds = sedesUsuario.map((s) => s.id_sede);
  const sedesDisponibles  = todasSedes.filter((s) => !sedesAsignadasIds.includes(s.id));

  const filteredUsers = users.filter(
    (u) =>
      (u.nombre?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (u.identificacion?.toString() || "").includes(search)
  );

  return (
    <Page>
      {/* ══ PANEL IZQUIERDO ══ */}
      <Left>
        <HeaderRow>
          <h3 style={{ margin: 0, fontSize: "13px" }}>Usuarios</h3>
          <BtnGeneral bg="cyan" padding="4px 8px" radius="5px" fw="600" fs="11px" onClick={handleNew}>
            Nuevo
          </BtnGeneral>
        </HeaderRow>

        <SearchInput placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />

        <RoleListContainer>
          {filteredUsers.map((u) => (
            <RoleItem
              key={u.Id_usuario}
              active={selectedUser === u.Id_usuario}
              onClick={() => handleSelectUser(u.Id_usuario)}
            >
              <div>
                <strong style={{ fontSize: "12px" }}>{u.nombre}</strong>
                <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "1px" }}>{u.identificacion}</div>
              </div>
            </RoleItem>
          ))}
        </RoleListContainer>
      </Left>

      {/* ══ PANEL DERECHO ══ */}
      <Right style={{ overflowY: "auto", padding: "8px 10px" }}>
        <HeaderRow style={{ marginBottom: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: "600", color: "#334155" }}>
            {form.id ? `Editor: ${form.nombre || "Usuario"}` : "Nuevo Usuario"}
          </span>
          <BtnGeneral bg="green" padding="4px 16px" radius="5px" fw="600" fs="11px" onClick={saveUser}>
            Guardar
          </BtnGeneral>
        </HeaderRow>

        {/* ── Sección: Datos del Usuario ── */}
        <Accordion title="Datos del Usuario" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            <Field lbl="ID:">
              <input style={{ ...inp, background: "#f8fafc", color: "#94a3b8" }}
                readOnly value={form.id} placeholder="(Asignado automáticamente)" />
            </Field>
            <Field lbl="Cédula:" required>
              <input style={inp} value={form.cedula} placeholder="Ej: 12345678"
                onChange={(e) => setForm({ ...form, cedula: e.target.value })} />
            </Field>
            <Field lbl="Nombre completo:" required>
              <input style={inp} value={form.nombre} placeholder="Juan Pérez"
                onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            </Field>
            <Field lbl="Correo electrónico:" required>
              <input style={inp} type="email" value={form.correo} placeholder="usuario@empresa.com"
                onChange={(e) => setForm({ ...form, correo: e.target.value })} />
            </Field>
          </div>
        </Accordion>

        {/* ── Sección: Acceso y Seguridad ── */}
        <Accordion title="Acceso y Seguridad" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            <Field lbl="Nombre de usuario:" required>
              <input style={inp} value={form.usuario} placeholder="jperez"
                onChange={(e) => setForm({ ...form, usuario: e.target.value })} />
            </Field>
            <Field lbl="Contraseña:" required>
              <div style={{ position: "relative" }}>
                <input type={showPass ? "text" : "password"}
                  style={{ ...inp, paddingRight: "100px" }}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <div style={{ position: "absolute", right: "3px", top: "50%", transform: "translateY(-50%)", display: "flex", gap: "3px" }}>
                  <BtnGeneral bg="gray" padding="2px 7px" radius="3px" fs="10px" fw="600" onClick={() => setShowPass(!showPass)}>
                    {showPass ? "Ocultar" : "Ver"}
                  </BtnGeneral>
                  <BtnGeneral bg="teal" padding="2px 7px" radius="3px" fs="10px" fw="600" onClick={() => setForm({ ...form, password: generatePassword() })}>
                    Generar
                  </BtnGeneral>
                </div>
              </div>
            </Field>
            <Field lbl="Rol:" required>
              <SearchableSelect
                key={`select-rol-${form.id || 'new'}`}
                value={form.rol_id ? String(form.rol_id) : ""}
                onValueChange={(val) => setForm({ ...form, rol_id: val })}
                options={roles}
                getOptionValue={(r) => String(r.id_rol)}
                getOptionLabel={(r) => r.nombre}
                placeholder="Seleccione rol..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>
            <Field lbl="Estado:">
              <SearchableSelect
                key={`select-estado-${form.id || 'new'}`}
                value={String(form.estado ?? "1")}
                onValueChange={(val) => setForm({ ...form, estado: val })}
                options={[
                  { value: "1", label: "ACTIVO" },
                  { value: "0", label: "INACTIVO" },
                ]}
                placeholder="Seleccione estado..."
                multiple={false}
                searchable={false}
                sx={selectSx}
              />
            </Field>

            {/* ── Perfil del usuario: ocupa todo el ancho ── */}
            <div style={{ gridColumn: "1 / -1", marginTop: "4px" }}>
              <div style={{
                border: "1px solid #e2e8f0", borderRadius: "5px",
                padding: "8px 12px", background: "#f8fafc"
              }}>
                <div style={{ fontSize: "10px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px" }}>
                  Perfil del usuario
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <label style={chkLbl}>
                    <input
                      type="checkbox"
                      checked={!!form.es_vacunador}
                      onChange={(e) => setForm({ ...form, es_vacunador: e.target.checked ? 1 : 0 })}
                    />
                    Es vacunador
                  </label>
                  <label style={chkLbl}>
                    <input
                      type="checkbox"
                      checked={!!form.realiza_citologia}
                      onChange={(e) => setForm({ ...form, realiza_citologia: e.target.checked ? 1 : 0 })}
                    />
                    Programa de citología
                  </label>
                </div>
              </div>
            </div>

          </div>
        </Accordion>

        {/* ── Sección: Sedes Autorizadas (solo al editar) ── */}
        {form.id && (
          <Accordion title="Sedes Autorizadas" count={sedesUsuario.length} defaultOpen={true}>
            {/* Fila para asignar nueva sede */}
            <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
              <SearchableSelect
                value={sedeAgg ? String(sedeAgg) : ""}
                onValueChange={(val) => setSedeAgg(val)}
                options={sedesDisponibles}
                getOptionValue={(s) => String(s.id)}
                getOptionLabel={(s) => `${s.nombreSede} — ${s.nombreEmpresa}`}
                placeholder="Seleccionar sede para agregar..."
                multiple={false}
                searchable={true}
                sx={{
                  flex: 1,
                  ...selectSx,
                }}
              />
              <BtnGeneral bg="teal" padding="3px 10px" radius="4px" fs="11px" fw="600" onClick={handleAgregarSede}>
                + Asignar
              </BtnGeneral>
            </div>

            {/* Tabla de sedes */}
            {sedesUsuario.length === 0 ? (
              <p style={{ color: "#94a3b8", fontSize: "11px", textAlign: "center", padding: "14px 0", margin: 0 }}>
                Sin sedes asignadas. Use el selector superior para agregar.
              </p>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={th}>Nombre de la Sede</th>
                    <th style={th}>Empresa</th>
                    <th style={th}>Ubicación</th>
                    <th style={th}>Asignación</th>
                    <th style={{ ...th, textAlign: "center", width: "52px" }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {sedesUsuario.map((s) => (
                    <tr key={s.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={td}>
                        <strong style={{ fontSize: "11px", color: "#1e293b" }}>{s.nombreSede}</strong>
                      </td>
                      <td style={td}>{s.nombreEmpresa}</td>
                      <td style={td}>{s.ciudad}</td>
                      <td style={td}>{new Date(s.fechaAsignacion).toLocaleDateString()}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <BtnGeneral bg="red" padding="2px 6px" radius="4px" fs="10px" onClick={() => handleQuitarSede(s.id)} title="Quitar sede">
                          ✕
                        </BtnGeneral>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Accordion>
        )}
      </Right>
    </Page>
  );
}

/* ─── Estilos idénticos a los de ReportManager ─── */
const inp     = { flex: 1, padding: "3px 7px", borderRadius: "4px", border: "1px solid #cbd5e1", fontSize: "11px", boxSizing: "border-box", outline: "none", marginBottom: 0 };
const tbl     = { width: "100%", borderCollapse: "collapse", fontSize: "11px" };
const th      = { padding: "3px 6px", fontWeight: "600", color: "#64748b", textAlign: "left", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em" };
const td      = { padding: "4px 6px", verticalAlign: "middle" };
const btnAdd  = { background: "none", border: "1px dashed #94a3b8", borderRadius: "4px", padding: "2px 10px", cursor: "pointer", color: "#64748b", fontSize: "10px", whiteSpace: "nowrap" };
const btnDel  = { background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "12px", padding: "0 3px" };
const chkLbl  = { display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#475569", cursor: "pointer" };
const btnAction = { background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "3px", padding: "2px 7px", cursor: "pointer", fontSize: "10px", fontWeight: "600" };

const selectSx = {
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    height: "26px",
    minHeight: "26px",
    borderRadius: "4px",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
    backgroundColor: "#ffffff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiSelect-select": {
    padding: "2px 8px !important",
    display: "flex",
    alignItems: "center",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
  },
};
